/* eslint-disable sort-keys */
import thirdPartyManager from '../managers/ThirdPartyManager';
import userManager from '../managers/UserManager';

import { registerClass } from '../SatCoreRegistry';
import { getStyleVar } from '../utils';

/**
 * All methods in this service can be overridden via a custom `ThirdPartyServiceOverrideService` for any given satellite.
 *
 * See `c2c-our/src/services/ThirdPartyServiceOverride.js` for an example.
 */
export default class ThirdPartyService {
  /** Can be overridden via `ThirdPartyServiceOverride` for a given satellite. */
  static shouldAllowThirdPartyChatLauncherForActiveUser = () => {
    const allowedChatUserRoles = thirdPartyManager.allowedThirdPartyChatLauncherUserRoles;
    return allowedChatUserRoles?.includes?.(userManager.activePermissionId);
  }

  /** Can be overridden via `ThirdPartyServiceOverride` for a given satellite. */
  static removeThirdPartyChatLauncherIfApplicable = ({ alsoRegenerateChatLauncherId = false } = {}) => {
    // Remove HubSpot Chat if present
    if (window.HubSpotConversations) {
      try {
        window.HubSpotConversations.widget.remove();
        const hubspotScriptLoader = document.getElementById('hs-script-loader');
        if (hubspotScriptLoader) {
          hubspotScriptLoader.remove();
          if (alsoRegenerateChatLauncherId) {
            thirdPartyManager.regenerateThirdPartyChatLauncherId();
          }
        }
      } catch (error) {
        console.warn('Error removing HubSpot Chat:', error);
      }
    }
    // Remove ZohoChat if present
    const zohoChatElement = document.getElementById('zohohc-asap-web-helper-main');
    if (zohoChatElement) {
      zohoChatElement.remove();
      if (alsoRegenerateChatLauncherId) {
        thirdPartyManager.regenerateThirdPartyChatLauncherId();
      }
    }
    // Remove HelpScout Beacon if present
    if (window.Beacon) {
      try {
        window.Beacon('destroy');
      } catch (error) {
        console.warn('Error destroying HelpScout Beacon:', error);
      }
      if (alsoRegenerateChatLauncherId) {
        thirdPartyManager.regenerateThirdPartyChatLauncherId();
      }
    }
  }

  /**
   * Can be overridden via `ThirdPartyServiceOverride` for a given satellite.
   *
   * This method provides default initialization logic for common third-party chat tools.
   * You can either:
   * 1. Override this method completely in ThirdPartyServiceOverride.initAdditionalThirdPartyChatLauncherLogic() for a given satellite.
   * or
   * 2. Add new conditional logic here based on thirdPartyChatLauncherCustomVariables settings (e.g. HelpScout, HubSpot).
   */
  static initAdditionalThirdPartyChatLauncherLogic = () => {
    const {
      helpScoutBeaconId,
      helpScoutBeaconUrl,
      hubSpotAccountId
    } = thirdPartyManager.thirdPartyChatLauncherCustomVariables || {};

    if (hubSpotAccountId) {
      ThirdPartyService.initHubSpotChatLogic();
    }

    if (helpScoutBeaconId && helpScoutBeaconUrl) {
      ThirdPartyService.initHelpScoutBeaconLogic();
    }
    // NOTE: This method can be expanded to support other conditional configurations as needed.
  }

  /** HubSpot Chat initialization logic */
  static initHubSpotChatLogic = () => {
    const { hubSpotAccountId } = thirdPartyManager.thirdPartyChatLauncherCustomVariables || {};

    // Create and inject HubSpot script
    const hubSpotScript = document.createElement('script');
    hubSpotScript.type = 'text/javascript';
    hubSpotScript.id = 'hs-script-loader';
    hubSpotScript.async = true;
    hubSpotScript.defer = true;
    hubSpotScript.src = `//js-na1.hs-scripts.com/${hubSpotAccountId}.js`;
    document.head.appendChild(hubSpotScript);

    // Wait for HubSpot to be ready, then load the widget if it is not already loaded
    const ALLOWED_TIMES_TRIED = 10;
    let timesTried = 0;

    const interval = setInterval(() => {
      if (window.HubSpotConversations) {
        try {
          // Check if widget is already loaded, if not load it
          const widgetStatus = window.HubSpotConversations.widget.status();
          if (widgetStatus?.loaded !== true) {
            window.HubSpotConversations.widget.load();
          }
          // Ensure widget starts in closed state
          window.HubSpotConversations.widget.close();
          clearInterval(interval);
        } catch (error) {
          // If status() fails, try to load anyway
          try {
            window.HubSpotConversations.widget.load();
            // Ensure widget starts in closed state
            window.HubSpotConversations.widget.close();
          } catch (loadError) {
            console.warn('Error loading HubSpot Chat widget:', loadError);
          }
          clearInterval(interval);
        }
      } else {
        timesTried++;
        if (timesTried >= ALLOWED_TIMES_TRIED) {
          console.warn('HubSpot initialization failed after', ALLOWED_TIMES_TRIED, 'attempts');
          clearInterval(interval);
        }
      }
    }, 500);
  }

  /** HelpScout Beacon initialization logic */
  static initHelpScoutBeaconLogic = () => {
    const {
      helpScoutBeaconId, helpScoutBeaconUrl
    } = thirdPartyManager.thirdPartyChatLauncherCustomVariables || {};
    // Create and inject HelpScout Beacon bootstrap (loader) script
    const bootstrapScript = document.createElement('script');
    bootstrapScript.type = 'text/javascript';
    bootstrapScript.innerHTML = `
      !function(e, t, n) {
        function a() {
          var e = t.getElementsByTagName("script")[0],
              n = t.createElement("script");
          n.type = "text/javascript";
          n.async = !0;
          n.src = "${helpScoutBeaconUrl}";
          e.parentNode.insertBefore(n, e);
        }
        if (e.Beacon = n = function(t, n, a) {
          e.Beacon.readyQueue.push({method: t, options: n, data: a});
        }, n.readyQueue = [], "complete" === t.readyState) return a();
        e.attachEvent ? e.attachEvent("onload", a) : e.addEventListener("load", a, !1);
      }(window, document, window.Beacon || function() {});
    `;
    document.head.appendChild(bootstrapScript);

    // Initialize when ready with retry logic and error handling
    const ALLOWED_TIMES_TRIED = 10;
    let timesTried = 0;
    // note: helpScout appears to subtract a number from what is given for zIndex.
    // e.g. if we send 1803 for zIndex, it appears to show up in the DOM as 1802.
    const zIndex = getStyleVar('--third-party-chat-launcher-icon-z-index');
    const interval = setInterval(() => {
      if (window.Beacon) {
        try {
          window.Beacon('init', helpScoutBeaconId);
          window.Beacon('config', {
            display: {
              style: 'icon',
              zIndex
            }
          });
          clearInterval(interval);
        } catch (error) {
          console.warn('Error initializing HelpScout Beacon:', error);
          clearInterval(interval);
        }
      } else {
        timesTried++;
        if (timesTried >= ALLOWED_TIMES_TRIED) {
          console.warn('HelpScout initialization failed after', ALLOWED_TIMES_TRIED, 'attempts');
          clearInterval(interval);
        }
      }
    }, 500);
  }
}

registerClass('ThirdPartyService', ThirdPartyService);
